import { signal } from '@preact/signals';

/**
 * A single polite live region for the whole app.
 *
 * Most of what this app does in response to a tap changes something a sighted user can see —
 * a button flips to a tick, a row slides out of the queue — and nothing a screen reader is
 * told about. Rather than sprinkling `role="status"` nodes that mount and unmount (a region
 * added to the DOM at announce time is unreliably announced, since assistive tech watches
 * regions that already exist), the app renders one region at the root and pushes text into it.
 *
 * The counter forces a fresh announcement when the same message repeats — adding two episodes
 * to the queue should say "Added to queue" twice, and identical text alone would not re-fire.
 */

export interface Announcement {
  message: string;
  /** Bumped on every announce so repeated identical text still re-triggers the region. */
  token: number;
}

export const announcement = signal<Announcement>({ message: '', token: 0 });

let token = 0;

/** Queue a polite announcement. Rude, urgent interruptions are deliberately not offered. */
export function announce(message: string): void {
  if (!message) return;
  announcement.value = { message, token: ++token };
}

/** Clear the region, e.g. when the thing it described is gone. Exposed for tests. */
export function clearAnnouncements(): void {
  announcement.value = { message: '', token: ++token };
}
